<template>
  <v-dialog :value="true" persistent scrollable max-width="900">
    <v-card :loading="carregando">
      <v-card-title class="text-h6">Efetivar matrícula</v-card-title>
      <v-card-text>
        <p>Criar a matrícula do aluno? Confira a turma e a série escolhidas pela família.</p>

        <v-row v-if="detalhe">
          <v-col cols="12" sm="6">
            <e-label>Turma</e-label>
            <v-select
              v-model="turmaId"
              :items="detalhe.turmas_para_efetivacao"
              item-text="descricao"
              item-value="id"
              dense
              solo
              hide-details="auto"
              @change="aoTrocarTurma"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <e-label>Série</e-label>
            <v-select
              v-model="serieId"
              :items="seriesDaTurma"
              item-text="descricao"
              item-value="id"
              :error-messages="serieId ? [] : ['Escolha a série.']"
              dense
              solo
              hide-details="auto"
            />
          </v-col>
        </v-row>
        <v-alert
          v-if="detalhe && detalhe.outro_responsavel_com_o_mesmo_email"
          type="warning"
          outlined
          dense
          class="mt-3"
        >
          O e-mail do pagador ({{ detalhe.responsavel_email }}) já está no cadastro de
          {{ detalhe.outro_responsavel_com_o_mesmo_email.descricao }}
          (CPF {{ formatarCpf(detalhe.outro_responsavel_com_o_mesmo_email.cpf) }}). Confira se não é
          erro de digitação ou a mesma pessoa com outro CPF.
        </v-alert>
        <v-alert v-if="turmaFoiTrocada" type="warning" outlined dense class="mt-3">
          A matrícula será feita na turma escolhida. O valor já pago não é alterado.
        </v-alert>

        <template v-if="detalhe && detalhe.aluno_id">
          <v-row class="mt-2">
            <v-col cols="12" md="6">
              <e-cartao-responsavel
                titulo="Responsável financeiro atual"
                :linhas="linhasDoAtual"
                :diferentes="diferencas"
                texto-sem-dados="Aluno sem responsável cadastrado."
              />
            </v-col>
            <v-col cols="12" md="6">
              <e-cartao-responsavel
                titulo="Pagador da pré-matrícula"
                :linhas="linhasDoPagador"
                :diferentes="diferencas"
              />
            </v-col>
          </v-row>
          <p v-if="diferencas.length" class="caption mb-0">Em destaque, o que é diferente.</p>
          <v-checkbox v-model="substituirResponsavel" :label="textoDoCheckbox" hide-details />
        </template>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text :disabled="efetivando" @click="$emit('fechar')">Cancelar</v-btn>
        <v-btn
          color="success"
          :loading="efetivando"
          :disabled="carregando || efetivando || !detalhe || !serieId"
          @click="efetivar"
        >
          Efetivar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import ECartaoResponsavel from "./ECartaoResponsavel.vue";
import { linhasDoResponsavel, rotulosDiferentes } from "./comparacaoDeResponsavel";
import { formatarCpf, somenteDigitos } from "./helpers";

/**
 * Confirmação da efetivação. A secretaria pode corrigir turma e série (da mesma escola e ano
 * letivo) quando a família escolheu errado; a parcela paga não muda. Para aluno já cadastrado, a secretaria escolhe se o pagador da
 * pré-matrícula substitui o responsável financeiro atual (desmarcado por padrão). Aluno novo
 * nasce com o pagador como responsável, então não há escolha.
 */
export default {
  name: "EModalEfetivarPreMatricula",
  components: { ECartaoResponsavel },
  props: {
    preMatriculaId: { type: Number, required: true },
  },
  data() {
    return {
      detalhe: null,
      carregando: false,
      efetivando: false,
      substituirResponsavel: false,
      turmaId: null,
      serieId: null,
    };
  },
  computed: {
    seriesDaTurma() {
      const turma = this.detalhe.turmas_para_efetivacao.find((item) => item.id === this.turmaId);
      return turma ? turma.series : [];
    },
    turmaFoiTrocada() {
      return !!this.detalhe && this.turmaId !== this.detalhe.turma_id;
    },
    responsavelAtual() {
      return this.detalhe.aluno && this.detalhe.aluno.responsavel;
    },
    linhasDoAtual() {
      const atual = this.responsavelAtual;
      if (!atual) return null;
      return linhasDoResponsavel(
        {
          nome: atual.descricao,
          cpf: atual.cpf,
          email: atual.email,
          logradouro: atual.logradouro,
          numero: atual.numero,
          bairro: atual.bairro,
          cidade: atual.cidade,
          uf: atual.estado,
          cep: atual.cep,
        },
        this.siglaDaUf
      );
    },
    linhasDoPagador() {
      const d = this.detalhe;
      return linhasDoResponsavel(
        {
          nome: d.responsavel_nome,
          cpf: d.responsavel_cpf,
          email: d.responsavel_email,
          logradouro: d.responsavel_logradouro,
          numero: d.responsavel_numero,
          bairro: d.responsavel_bairro,
          cidade: d.responsavel_cidade,
          uf: d.responsavel_uf,
          cep: d.responsavel_cep,
        },
        this.siglaDaUf
      );
    },
    diferencas() {
      return this.linhasDoAtual ? rotulosDiferentes(this.linhasDoAtual, this.linhasDoPagador) : [];
    },
    mesmoCpf() {
      return (
        !!this.responsavelAtual &&
        somenteDigitos(this.responsavelAtual.cpf) === somenteDigitos(this.detalhe.responsavel_cpf)
      );
    },
    textoDoCheckbox() {
      return this.mesmoCpf
        ? "Atualizar os dados do responsável com os da pré-matrícula"
        : "Substituir o responsável financeiro do aluno pelo pagador da pré-matrícula";
    },
  },
  async mounted() {
    this.carregando = true;
    try {
      this.detalhe = await this.$services.preMatriculasService.pegar(this.preMatriculaId);
      this.turmaId = this.detalhe.turma_id;
      this.serieId = this.detalhe.serie_id;
    } catch (error) {
      this.$handleError(error);
      this.$emit("fechar");
    }
    this.carregando = false;
  },
  methods: {
    formatarCpf,
    // responsavels.estado guarda o nome por extenso; a pré-matrícula guarda a sigla.
    siglaDaUf(uf) {
      const estado = this.$constants.estadosList.find(
        (item) => item.descricao.toLowerCase() === `${uf}`.toLowerCase()
      );
      return estado ? estado.codigo : `${uf}`.toUpperCase();
    },
    aoTrocarTurma() {
      if (!this.seriesDaTurma.some((serie) => serie.id === this.serieId)) {
        this.serieId = this.seriesDaTurma.length === 1 ? this.seriesDaTurma[0].id : null;
      }
    },
    async efetivar() {
      if (!this.serieId) return;
      this.efetivando = true;
      try {
        // A API aceita a mesma turma da pré-matrícula; a correção só vale aqui, na efetivação.
        const resposta = await this.$services.preMatriculasService.efetivar(this.preMatriculaId, {
          substituir_responsavel: Boolean(this.detalhe.aluno_id) && this.substituirResponsavel,
          turma_id: this.turmaId,
          serie_id: this.serieId,
        });
        this.$emit("efetivada", resposta);
      } catch (error) {
        this.$handleError(error);
      }
      this.efetivando = false;
    },
  },
};
</script>
